import { useCallback, useEffect, useRef, useState } from 'react';
import { unwrap } from '@geefamily/api';
import { api } from '@/app/api';
import { useAuth } from './use-auth';
import type { components } from '@geefamily/api';
import type { AuthTokens } from './session';

interface StartResponse {
  requestId: string;
  deepLink: string;
  verificationCode: string;
  expiresAtUtc: string;
}

/** Comes straight from the OpenAPI document, so the union cannot drift. */
type TelegramStatus = components['schemas']['TelegramLoginStatusCode'];

interface StatusResponse {
  status: TelegramStatus;
  tokens?: AuthTokens | null;
  groupInviteUrl?: string | null;
  message?: string | null;
}

export interface TelegramLoginState {
  phase: 'idle' | 'starting' | TelegramStatus | 'failed';
  deepLink?: string | undefined;
  verificationCode?: string | undefined;
  groupInviteUrl?: string | undefined;
  message?: string | undefined;
  error?: string | undefined;
}

/** How often to ask the API whether the user has answered in Telegram. */
const PollIntervalMs = 2000;

/**
 * Signing IN with Telegram, or attaching Telegram to the account already signed
 * in. Same exchange, two endpoints and two endings.
 */
type TelegramFlow = 'login' | 'link';

/**
 * Drives a Telegram exchange: opens a request, then polls until the user
 * approves in the bot.
 *
 * Polling rather than a socket because the whole exchange lasts seconds and a
 * WebSocket for it would cost more than it saves. The interval is cleared on
 * every terminal state and on unmount, so a user who navigates away mid-flow
 * leaves nothing running.
 *
 * The two flows share everything except which endpoint opens the request and
 * what happens on approval — one starts a session, the other has one already and
 * must NOT touch it. Parameterised rather than copied: the polling, the cleanup
 * and the "one failed poll is a blip" rule are the parts worth getting right
 * once.
 */
function useTelegramFlow(flow: TelegramFlow, onDone?: () => void) {
  const { setSession } = useAuth();
  const [state, setState] = useState<TelegramLoginState>({ phase: 'idle' });
  const timerRef = useRef<number | null>(null);

  const stopPolling = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => stopPolling, [stopPolling]);

  const start = useCallback(async () => {
    stopPolling();
    setState({ phase: 'starting' });

    let started: StartResponse;

    try {
      // `/link` is authenticated and binds the request to the caller; `/start`
      // is anonymous and creates or finds an account.
      started = await unwrap<StartResponse>(
        flow === 'link'
          ? api.POST('/api/v1/identity/auth/telegram/link', {})
          : api.POST('/api/v1/identity/auth/telegram/start', {}),
      );
    } catch (error) {
      setState({
        phase: 'failed',
        error:
          error instanceof Error
            ? error.message
            : flow === 'link'
              ? 'Telegram bağlanamadı.'
              : 'Telegram girişi başlatılamadı.',
      });
      return;
    }

    setState({
      phase: 'pending',
      deepLink: started.deepLink,
      verificationCode: started.verificationCode,
    });

    timerRef.current = window.setInterval(() => {
      void (async () => {
        try {
          const status = await unwrap<StatusResponse>(
            api.POST('/api/v1/identity/auth/telegram/poll', {
              body: { requestId: started.requestId },
            }),
          );

          if (status.status === 'approved') {
            stopPolling();

            // Linking returns no tokens: the caller is already signed in, and
            // replacing their session here would be a second, pointless login.
            if (flow === 'login' && status.tokens) {
              setSession(status.tokens.user, status.tokens.accessToken);
            }

            setState((current) => ({ ...current, phase: 'approved' }));
            onDone?.();
            return;
          }

          if (status.status === 'denied' || status.status === 'expired') {
            stopPolling();
          }

          setState((current) => ({
            ...current,
            phase: status.status,
            groupInviteUrl: status.groupInviteUrl ?? undefined,
            message: status.message ?? undefined,
          }));
        } catch (error) {
          // A single failed poll is usually a blip; stopping on it would strand
          // a user who is about to approve. The request's own expiry ends the
          // loop if the API is genuinely gone.
          if (error instanceof Error && error.message.includes('not configured')) {
            stopPolling();
            setState({ phase: 'failed', error: error.message });
          }
        }
      })();
    }, PollIntervalMs);
  }, [flow, onDone, setSession, stopPolling]);

  const reset = useCallback(() => {
    stopPolling();
    setState({ phase: 'idle' });
  }, [stopPolling]);

  return { state, start, reset };
}

/** Signs in with Telegram, creating the account if there is not one. */
export function useTelegramLogin(onSignedIn?: () => void) {
  return useTelegramFlow('login', onSignedIn);
}

/** Attaches Telegram to the account already signed in. */
export function useTelegramLink(onLinked?: () => void) {
  return useTelegramFlow('link', onLinked);
}
